import React from 'react';
import { FunctionComponent, useState, useEffect, useCallback, SetStateAction, useRef, Dispatch } from 'react';
import styled from 'styled-components/macro';
import { useGetTeamMembersMentionListQuery } from 'shared/types/graphql';
import { ClinicAvatar } from 'shared/components/Avatars';
import useClinicUser from 'shared/hooks/useClinicUser';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import getCoordinates from 'textarea-caret';
import Dropdown, { IOption, PopoverPlacement } from 'shared/components/Dropdown';
import { Button } from '@televet/kibble-ui/build/components/Button';

export interface IMentionedUser {
  name: string;
  id: string;
}

interface IMentionsList {
  inputTextareaRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  isListVisibile: boolean;
  setDraftMessageText: (value: React.SetStateAction<string>) => void;
  setIsListVisible: Dispatch<SetStateAction<boolean>>;
  setMentionedUsers: Dispatch<SetStateAction<{ name: string; id: string }[]>>;
}

const MentionsList: FunctionComponent<IMentionsList> = ({
  inputTextareaRef,
  setIsListVisible,
  setDraftMessageText,
  setMentionedUsers,
}) => {
  const { currentClinicId } = useClinicUser();
  const hiddenButtonRef = useRef<HTMLButtonElement | null>(null);
  const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });
  const [teamMembersList, setTeamMembersList] = useState<IOption[]>([]);
  const [coordinates, setCoordinates] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const { data: teamMembers, loading: loadingTeamMembers } = useGetTeamMembersMentionListQuery({
    variables: {
      where: {
        vetInfo: {
          clinics: {
            some: {
              id: {
                equals: currentClinicId,
              },
            },
          },
        },
        isActive: {
          equals: true,
        },
      },
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  useEffect(() => {
    if (hiddenButtonRef) {
      hiddenButtonRef?.current?.click();
    }
  }, []);

  const determineCursorPositionAndCoordinates = useCallback(() => {
    const textarea = inputTextareaRef?.current;
    if (textarea) {
      const caret = getCoordinates(textarea, textarea?.selectionEnd);

      setCoordinates({
        top: caret.top,
        left: caret.left,
      });

      setCursorPosition({
        start: textarea.selectionStart + 1,
        end: textarea.selectionEnd + 1,
      });
    }
  }, [inputTextareaRef]);

  const loadTeamMembers = useCallback(() => {
    if (!loadingTeamMembers && teamMembers?.findManyUser?.length) {
      teamMembers?.findManyUser?.forEach((teamMember) => {
        const option = {
          text: teamMember.nameDisplay ? `${teamMember.nameDisplay}` : `${teamMember.firstName} ${teamMember.lastName}`,
          value: teamMember.id,
          icon: <ClinicAvatar clinicMember={teamMember} size="xs" />,
        };
        setTeamMembersList((previousState) => [...previousState, option]);
      });
    }
  }, [loadingTeamMembers, teamMembers]);

  useEffect(() => {
    loadTeamMembers();
    determineCursorPositionAndCoordinates();
  }, [loadTeamMembers, determineCursorPositionAndCoordinates]);

  const setNewCursorPosition = useCallback(
    (offset = 0) => {
      const textarea = inputTextareaRef.current;

      if (textarea) {
        const newEndPos = cursorPosition.end + offset;
        textarea.focus();

        setTimeout(() => {
          textarea.setSelectionRange(newEndPos, newEndPos);
        }, 0);
      }
    },
    [cursorPosition, inputTextareaRef],
  );

  const onTeamMemberSelect = useCallback(
    (e: React.MouseEvent<Element, globalThis.MouseEvent>, option: IOption) => {
      e.preventDefault();
      setDraftMessageText((value) => {
        return `${value.slice(0, cursorPosition.start)}${option.text} ${value.slice(cursorPosition.end)}`;
      });
      setNewCursorPosition(option.text.length + 1);

      setMentionedUsers((prevUsers) => [...prevUsers, { name: option.text, id: option.value }]);
      setIsListVisible(false);
    },
    [setDraftMessageText, setIsListVisible, setMentionedUsers, cursorPosition, setNewCursorPosition],
  );

  return (
    <Container left={coordinates.left}>
      <Dropdown
        showSearch={true}
        options={teamMembersList}
        onSelect={onTeamMemberSelect}
        onCloseCallback={(searchInput: string): void => {
          setDraftMessageText((value) => {
            return `${value}${searchInput} `;
          });
          setIsListVisible(false);
          setNewCursorPosition(0);
        }}
        closeOnClick={true}
        placement={PopoverPlacement.TopStart}
      >
        <span>
          <HiddenButton ref={hiddenButtonRef} variant="ghostNeutral" />
        </span>
      </Dropdown>
    </Container>
  );
};

export default MentionsList;

const Container = styled.div<{
  top?: number;
  left: number;
}>`
  position: absolute;
  left: ${({ left }): string => `${left}px`};
  overflow: visible;
  height: 0;
  width: 300px;
`;

const HiddenButton = styled(Button)`
  height: 0;
  visibility: hidden;
`;
